import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { apiSites } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { isAdminNational } from '@/constants/permits';
import ExportImportButtons from '@/components/ExportImportButtons';
import { Building2, MapPin, Plus, Pencil, Trash2, Phone, User as UserIcon, X, Save, Mail } from 'lucide-react';

const TYPES = ['Cimenterie', 'Centre Broyage', 'Dépôt', 'Carrière', 'Siège'];

function SiteForm({ initial, onSubmit, onCancel, busy }) {
  const [form, setForm] = useState({
    code: initial?.code || '',
    name: initial?.name || '',
    type: initial?.type || 'Cimenterie',
    region: initial?.region || '',
    emergency: initial?.emergency || '',
    adresse: initial?.adresse || '',
    correspondant_principal: initial?.correspondant_principal || '',
    correspondant_principal_portable: initial?.correspondant_principal_portable || '',
    correspondant_principal_email: initial?.correspondant_principal_email || '',
  });
  const isEdit = !!initial;
  const update = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="site-form-overlay">
      <div className="bg-white border-2 border-stone-900 max-w-2xl w-full mt-10 p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-display font-black tracking-tight">{isEdit ? 'Modifier le site' : 'Nouveau site'}</h2>
          <button onClick={onCancel} className="text-stone-500 hover:text-[#DA291C]" data-testid="site-form-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label-industrial">Code *</label>
            <input
              data-testid="site-form-code"
              type="text"
              className="input-industrial w-full uppercase"
              value={form.code}
              disabled={isEdit}
              onChange={(e) => update('code', e.target.value.toUpperCase())}
              placeholder="Ex. LTE"
              maxLength={6}
            />
          </div>
          <div>
            <label className="label-industrial">Type *</label>
            <select
              data-testid="site-form-type"
              className="input-industrial w-full"
              value={form.type}
              onChange={(e) => update('type', e.target.value)}
            >
              {TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="label-industrial">Nom complet *</label>
            <input
              data-testid="site-form-name"
              type="text"
              className="input-industrial w-full"
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="Ex. Saint-Pierre-La-Cour"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="label-industrial">Adresse</label>
            <input
              data-testid="site-form-adresse"
              type="text"
              className="input-industrial w-full"
              value={form.adresse}
              onChange={(e) => update('adresse', e.target.value)}
              placeholder="Ex. Rte de Bréal, 53410 Saint-Pierre-la-Cour"
            />
          </div>
          <div>
            <label className="label-industrial">Région</label>
            <input
              data-testid="site-form-region"
              type="text"
              className="input-industrial w-full"
              value={form.region}
              onChange={(e) => update('region', e.target.value)}
              placeholder="Ex. Pays de la Loire"
            />
          </div>
          <div>
            <label className="label-industrial">Numéro d'urgence</label>
            <input
              data-testid="site-form-emergency"
              type="tel"
              className="input-industrial w-full"
              value={form.emergency}
              onChange={(e) => update('emergency', e.target.value)}
              placeholder="02 43 66 41 41"
            />
          </div>
          <div className="sm:col-span-2 border-t border-stone-200 pt-4">
            <div className="label-industrial mb-3">Correspondant principal du site</div>
          </div>
          <div>
            <label className="label-industrial">Nom</label>
            <input
              data-testid="site-form-correspondant"
              type="text"
              className="input-industrial w-full"
              value={form.correspondant_principal}
              onChange={(e) => update('correspondant_principal', e.target.value)}
              placeholder="LHOTELLIER Philippe"
            />
          </div>
          <div>
            <label className="label-industrial">Portable</label>
            <input
              data-testid="site-form-correspondant-portable"
              type="tel"
              className="input-industrial w-full"
              value={form.correspondant_principal_portable}
              onChange={(e) => update('correspondant_principal_portable', e.target.value)}
              placeholder="06 98 34 45 81"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="label-industrial">Email</label>
            <input
              data-testid="site-form-correspondant-email"
              type="email"
              className="input-industrial w-full"
              value={form.correspondant_principal_email}
              onChange={(e) => update('correspondant_principal_email', e.target.value)}
              placeholder="philippe.lhotellier@lafarge.com"
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t border-stone-200">
          <button
            onClick={onCancel}
            disabled={busy}
            className="btn-industrial-secondary"
            data-testid="site-form-cancel"
          >
            Annuler
          </button>
          <button
            onClick={() => onSubmit(form)}
            disabled={busy || !form.code || !form.name}
            className="btn-industrial-primary flex items-center gap-2"
            data-testid="site-form-save"
          >
            <Save className="w-4 h-4" /> {isEdit ? 'Enregistrer' : 'Créer le site'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Sites() {
  const { user } = useAuth();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} for new | site obj for edit
  const [busy, setBusy] = useState(false);
  const isAdmin = isAdminNational(user);
  const isHseSite = !!user && (user.roles || []).includes('responsable_hse_site');

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiSites.list();
      // Responsable HSE Site → restreindre la vue à ses sites uniquement
      let visible = r.data;
      if (!isAdmin && isHseSite) {
        const mySites = new Set(user?.sites || []);
        visible = r.data.filter((s) => mySites.has(s.code));
      }
      setSites(visible);
    } catch {
      toast.error('Impossible de charger les sites');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (form) => {
    setBusy(true);
    try {
      if (editing && editing.code && sites.some((s) => s.code === editing.code)) {
        await apiSites.update(editing.code, form);
        toast.success('Site mis à jour');
      } else {
        await apiSites.create(form);
        toast.success('Site créé');
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur enregistrement');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Supprimer définitivement le site ${code} ?`)) return;
    try {
      await apiSites.remove(code);
      toast.success('Site supprimé');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Suppression impossible');
    }
  };

  const grouped = sites.reduce((acc, s) => {
    const region = s.region || 'Non classé';
    (acc[region] = acc[region] || []).push(s);
    return acc;
  }, {});

  return (
    <div className="max-w-7xl space-y-8" data-testid="sites-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-stone-500 mb-2">
            Référentiel
          </div>
          <h1 className="text-4xl font-display font-black tracking-tighter">Sites Lafarge Ciments France</h1>
          <p className="text-stone-600 mt-3">{sites.length} établissements répartis sur le territoire national.</p>
        </div>
        {isAdmin ? (
          <div className="inline-flex items-center gap-3">
            <ExportImportButtons
              exportPath="/admin/export/sites.csv"
              importPath="/admin/import/sites"
              filenameHint="sites_lafarge.csv"
              visible={isAdmin}
              onImported={() => load()}
            />
            <button
              data-testid="sites-add-btn"
              onClick={() => setEditing({})}
              className="btn-industrial-primary flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Nouveau site
            </button>
          </div>
        ) : isHseSite && (
          <div className="text-xs text-stone-500 italic">Vue restreinte à votre périmètre</div>
        )}
      </div>

      {loading ? <div className="text-stone-500">Chargement...</div> : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([region, list]) => (
            <div key={region}>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px flex-1 bg-stone-300"></div>
                <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-stone-600">{region}</div>
                <div className="h-px flex-1 bg-stone-300"></div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {list.map((s) => (
                  <div key={s.code} data-testid={`site-${s.code}`} className="section-card hover:border-[#09090B] hover:border-2 transition-all group">
                    <div className="flex items-start justify-between">
                      <Building2 className="w-6 h-6 text-[#DA291C]" strokeWidth={2.5} />
                      <span className="font-mono text-[10px] bg-stone-100 px-2 py-0.5 font-bold">{s.code}</span>
                    </div>
                    <div className="text-lg font-display font-bold mt-3 text-[#09090B]">{s.name}</div>
                    <div className="text-xs text-stone-500 uppercase tracking-wider mt-1">{s.type}</div>
                    {s.adresse && (
                      <div className="flex items-start gap-1.5 text-xs text-stone-600 mt-3">
                        <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <span className="leading-snug">{s.adresse}</span>
                      </div>
                    )}
                    {s.emergency && (
                      <div className="flex items-center gap-1.5 text-xs text-stone-700 mt-1.5 font-semibold">
                        <Phone className="w-3 h-3 text-[#DA291C]" />
                        {s.emergency}
                      </div>
                    )}
                    {s.correspondant_principal && (
                      <div className="flex items-center gap-1.5 text-xs text-stone-700 mt-1.5">
                        <UserIcon className="w-3 h-3" />
                        <span className="font-semibold">{s.correspondant_principal}</span>
                      </div>
                    )}
                    {s.correspondant_principal_email && (
                      <div className="flex items-center gap-1.5 text-[10px] text-stone-500 mt-1">
                        <Mail className="w-3 h-3" />
                        {s.correspondant_principal_email}
                      </div>
                    )}
                    {(isAdmin || isHseSite) && (
                      <div className="flex gap-2 mt-4 pt-3 border-t border-stone-100">
                        <button
                          data-testid={`site-edit-${s.code}`}
                          onClick={() => setEditing(s)}
                          className="text-xs uppercase font-bold tracking-wider text-stone-700 hover:text-[#09090B] flex items-center gap-1"
                        >
                          <Pencil className="w-3 h-3" /> Modifier
                        </button>
                        {isAdmin && (
                          <button
                            data-testid={`site-delete-${s.code}`}
                            onClick={() => handleDelete(s.code)}
                            className="text-xs uppercase font-bold tracking-wider text-[#DA291C] hover:text-red-700 flex items-center gap-1 ml-auto"
                          >
                            <Trash2 className="w-3 h-3" /> Supprimer
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <SiteForm
          initial={editing.code ? editing : null}
          onSubmit={handleSubmit}
          onCancel={() => setEditing(null)}
          busy={busy}
        />
      )}
    </div>
  );
}
